import { Banknote, UserPlus, Eye } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface FeeDisplayProps {
  /** Phí tham gia cho tất cả người chơi */
  participationFee?: number;
  /** Phí gia nhập cho người xin vào */
  recruitingFee?: number;
  /** Phí khán giả */
  spectatorFee?: number;
  /** Loại trận: 1v1 hoặc team */
  matchType?: '1v1' | 'team';
  /** Trận đấu có đang tuyển thêm người không */
  isRecruiting?: boolean;
  /** Trận đấu có cho phép khán giả không */
  allowSpectators?: boolean;
  /** Hiển thị dạng compact (chỉ icon + giá) */
  compact?: boolean;
  /** Custom class */
  className?: string;
}

const formatFee = (fee: number) => {
  if (fee === 0) return 'Miễn phí';
  return `${new Intl.NumberFormat('vi-VN').format(fee)}đ`;
};

export function FeeDisplay({
  participationFee = 0,
  recruitingFee = 0,
  spectatorFee = 0,
  matchType = '1v1',
  isRecruiting = false,
  allowSpectators = false,
  compact = false,
  className = '',
}: FeeDisplayProps) {
  const feeUnit = matchType === '1v1' ? '/người' : '/đội';

  if (compact) {
    // Compact mode: single line with just participation fee
    return (
      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full bg-sport-tennis/20 text-sport-tennis ${className}`}>
        {participationFee > 0 ? `${formatFee(participationFee)}${feeUnit}` : 'Miễn phí'}
      </span>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={`flex flex-col gap-1 text-xs ${className}`}>
        {/* Phí tham gia */}
        <Tooltip>
          <TooltipTrigger asChild>
            {participationFee > 0 ? (
              <span className="flex items-center gap-1.5 text-sport-tennis font-medium cursor-help">
                <Banknote className="w-3.5 h-3.5" />
                <span className="text-muted-foreground">Phí tham gia:</span>
                <span>{formatFee(participationFee)}{feeUnit}</span>
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sport-tennis font-medium cursor-help">
                <Banknote className="w-3.5 h-3.5" />
                <span>Miễn phí tham gia</span>
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px]">
            <p className="text-xs">Phí mà tất cả người chơi đã được mời hoặc đăng ký từ đầu phải đóng để tham gia trận đấu</p>
          </TooltipContent>
        </Tooltip>

        {/* Phí gia nhập */}
        {isRecruiting && recruitingFee > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center gap-1.5 text-amber-500 font-medium cursor-help">
                <UserPlus className="w-3.5 h-3.5" />
                <span className="text-muted-foreground">Phí gia nhập:</span>
                <span>{formatFee(recruitingFee)}/người</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[250px]">
              <p className="text-xs">Phí dành cho người chơi mới xin tham gia vào trận đấu đang tuyển thêm người</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Phí khán giả */}
        {allowSpectators && spectatorFee > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center gap-1.5 text-purple-500 font-medium cursor-help">
                <Eye className="w-3.5 h-3.5" />
                <span className="text-muted-foreground">Phí khán giả:</span>
                <span>{formatFee(spectatorFee)}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[250px]">
              <p className="text-xs">Phí dành cho người muốn đến xem trận đấu với tư cách khán giả</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
